import { useEffect, useState } from 'react'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { featureFlagClientId } from 'Config'

/**
 * Upper bound on the wait for LaunchDarkly to initialise. If the SDK never settles
 * (blocked request, offline), consumers unblock with the default (all-off) values
 * rather than hiding flagged UI forever.
 */
const LD_READY_TIMEOUT_MS = 5000

/**
 * Returns true once flag values are reliable, so flagged UI can render its final state
 * instead of painting the default first and swapping on the SDK's first update.
 *
 * The login page is pre-authentication and never calls `identify()`, so readiness is just SDK
 * initialisation. `LDProvider` only starts that in `componentDidMount`, so `useLDClient()` is
 * undefined on the first render and the first effect pass - waiting for the client to appear is
 * part of the wait, not a reason to give up on it. With no client id configured there is no SDK
 * at all and every flag is off, so readiness is immediate.
 */
const useFeatureFlagsReady = () => {
  const ldClient = useLDClient()
  const [isReady, setIsReady] = useState(!featureFlagClientId)

  useEffect(() => {
    if (isReady) {
      return undefined
    }

    let isCancelled = false
    const markReady = () => {
      if (!isCancelled) {
        setIsReady(true)
      }
    }

    // Bounded even while the client is still undefined, so a provider that never mounts its
    // client cannot hide the logo indefinitely. Re-armed when `ldClient` arrives.
    const timeoutId = setTimeout(markReady, LD_READY_TIMEOUT_MS)

    if (ldClient) {
      ldClient.waitUntilReady().then(markReady).catch(markReady)
    }

    return () => {
      isCancelled = true
      clearTimeout(timeoutId)
    }
  }, [ldClient, isReady])

  return isReady
}

export default useFeatureFlagsReady
